import { useState, useMemo, useEffect } from 'react';
import styles from '../../styles/ModuleUsageCalendar.module.css';
import { 
  Paper,
  Typography,
  Box,
  Tabs,
  Tab,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  MenuItem, 
  Checkbox,
  ListItemText,
  Popover,
  IconButton,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Divider,
  Card,
  CardContent
} from '@mui/material';
import { styled } from '@mui/material/styles';
import FilterListIcon from '@mui/icons-material/FilterList';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import StarIcon from '@mui/icons-material/Star';

// Custom styled TableCell for better readability with hover effects
const StyledTableCell = styled(TableCell)(({ theme, ishighest, trend }) => ({
  padding: theme.spacing(1),
  textAlign: 'center',
  position: 'relative',
  ...(ishighest === 'true' && {
    backgroundColor: 'rgba(76, 175, 80, 0.2)', // Light green for highest values
    fontWeight: 'bold',
  }),
  ...(trend === 'up' && {
    backgroundColor: 'rgba(76, 175, 80, 0.15)',
  }),
  ...(trend === 'down' && {
    backgroundColor: 'rgba(244, 67, 54, 0.15)',
  }),
  ...(trend === 'neutral' && {
    backgroundColor: 'rgba(158, 158, 158, 0.08)', // Light gray for neutral values
  }),
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  }
}));

const AllActivityTable = ({ data }) => {
  const [granularity, setGranularity] = useState('daily');
  const [moduleFilters, setModuleFilters] = useState({});
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [columnSortBy, setColumnSortBy] = useState('');
  const [columnSortDir, setColumnSortDir] = useState('desc');
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  
  // Process modules function to extract unique modules from data
  const processModules = (inputData) => {
    if (!inputData || inputData.length === 0) return [];
    
    const modules = new Set();
    inputData.forEach(entry => {
      entry.modules.forEach(module => {
        modules.add(module.module);
      });
    });
    
    return Array.from(modules).sort();
  };
  
  // Helper functions for trend calculation
  const getTrend = (currentValue, previousValue) => {
    if (previousValue === undefined || previousValue === null) return 'neutral';
    if (currentValue > previousValue) return 'up';
    if (currentValue < previousValue) return 'down';
    return 'neutral';
  };
  
  const getPercentChange = (currentValue, previousValue) => {
    if (previousValue === undefined || previousValue === null || previousValue === 0) {
      return currentValue > 0 ? 100 : 0;
    }
    return Math.round(((currentValue - previousValue) / previousValue) * 100);
  };
  
  // Get all unique modules across all data entries
  const modules = useMemo(() => processModules(data), [data]);
  
  // Filter data based on selected granularity
  const getFilteredData = () => {
    if (!data || data.length === 0) return [];
    
    let filteredData = [...data];
    
    switch (granularity) {
      case 'weekly':
        filteredData = filteredData.filter(entry => 
          entry._id && entry._id.group && entry._id.group.includes('W')
        );
        // Sort by week number ascending
        filteredData.sort((a, b) => {
          const weekA = parseInt(a._id.group.split('-W')[1]);
          const weekB = parseInt(b._id.group.split('-W')[1]);
          return weekA - weekB;
        });
        break;
      case 'monthly':
        filteredData = filteredData.filter(entry => 
          entry._id && entry._id.group && entry._id.group.includes('M')
        );
        // Sort by month ascending
        filteredData.sort((a, b) => {
          return a._id.group.localeCompare(b._id.group);
        });
        break;
      default: // daily
        filteredData = filteredData.filter(entry => 
          entry._id && entry._id.group && 
          !entry._id.group.includes('W') && 
          !entry._id.group.includes('M')
        );
        // Sort by date descending (most recent first)
        filteredData.sort((a, b) => {
          return b._id.group.localeCompare(a._id.group);
        });
    }
    
    // Apply module filters
    if (Object.values(moduleFilters).some(val => val === false)) {
      filteredData = filteredData.map(entry => {
        const filteredModules = entry.modules.filter(
          mod => moduleFilters[mod.module] !== false
        );
        return { ...entry, modules: filteredModules };
      });
    }
    
    // Apply column sorting if active
    if (columnSortBy) {
      filteredData.sort((a, b) => {
        if (columnSortBy === 'date') {
          return columnSortDir === 'desc' 
            ? b._id.group.localeCompare(a._id.group) 
            : a._id.group.localeCompare(b._id.group);
        } else if (columnSortBy === 'total') {
          const sumA = a.modules.reduce((sum, m) => sum + m.count, 0);
          const sumB = b.modules.reduce((sum, m) => sum + m.count, 0);
          return columnSortDir === 'desc' ? sumB - sumA : sumA - sumB;
        }
        
        // For module columns
        const countA = a.modules.find(m => m.module === columnSortBy)?.count || 0;
        const countB = b.modules.find(m => m.module === columnSortBy)?.count || 0;
        
        return columnSortDir === 'desc' ? countB - countA : countA - countB;
      });
    }
    
    return filteredData;
  };
  
  const handleGranularityChange = (event, newValue) => {
    setGranularity(newValue);
  };
  
  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };
  
  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };
  
  const handleFilterChange = (module) => {
    setModuleFilters(prev => ({
      ...prev,
      [module]: !prev[module]
    }));
  };
  
  const handleInfoOpen = () => {
    setInfoDialogOpen(true);
  };
  
  const handleInfoClose = () => {
    setInfoDialogOpen(false);
  };
  
  const handleColumnSort = (columnName) => {
    if (columnSortBy === columnName) {
      // Toggle sort direction if clicking the same column
      setColumnSortDir(columnSortDir === 'desc' ? 'asc' : 'desc');
    } else {
      // Set new column and default to descending
      setColumnSortBy(columnName);
      setColumnSortDir('desc');
    }
  };
  
  const filteredData = getFilteredData();
  
  // Initialize module filters if not already set
  useEffect(() => {
    if (modules.length > 0 && Object.keys(moduleFilters).length === 0) {
      const initialFilters = {};
      modules.forEach(module => {
        initialFilters[module] = true;
      });
      setModuleFilters(initialFilters);
    }
  }, [modules, moduleFilters]);
  
  // Find the highest value for each module across all time periods
  const getHighestValue = () => {
    let highest = {};
    modules.forEach(module => {
      highest[module] = 0;
      filteredData.forEach(entry => {
        const moduleEntry = entry.modules.find(m => m.module === module);
        if (moduleEntry && moduleEntry.count > highest[module]) {
          highest[module] = moduleEntry.count;
        }
      });
    });
    return highest;
  };
  
  const highestValues = getHighestValue();
  
  // Filter modules based on active filters
  const visibleModules = modules.filter(module => moduleFilters[module] !== false);
  
  // Calculate counts for tab badges
  const dayCounts = data ? data.filter(entry => 
    entry._id && entry._id.group && !entry._id.group.includes('W') && !entry._id.group.includes('M')
  ).length : 0;
  
  const weekCounts = data ? data.filter(entry => 
    entry._id && entry._id.group && entry._id.group.includes('W')
  ).length : 0;
  
  const monthCounts = data ? data.filter(entry => 
    entry._id && entry._id.group && entry._id.group.includes('M')
  ).length : 0;

  // Check if a value is a peak compared to others
  const isPeak = (value, moduleData) => {
    if (!value || !moduleData || moduleData.length < 5) return false;
    
    const counts = moduleData.map(item => item.count || 0);
    const average = counts.reduce((sum, count) => sum + count, 0) / counts.length;
    const stdDev = Math.sqrt(counts.reduce((sum, count) => sum + Math.pow(count - average, 2), 0) / counts.length);
    
    // Value is a peak if it's more than 2 * stdDev above mean
    return value > average + 2 * stdDev;
  };

  return (
    <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Module Usage Calendar</Typography>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="View table information and legend">
            <IconButton size="small" onClick={handleInfoOpen} className={styles['info-icon']}>
              <InfoIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Filter modules">
            <IconButton size="small" onClick={handleFilterClick} className={styles['info-icon']}>
              <FilterListIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      
      {/* Table Information Dialog */}
      <Dialog 
        open={infoDialogOpen} 
        onClose={handleInfoClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px' }}>
          <Box component="span" sx={{ typography: 'h6' }}>Table Information and Legend</Box>
          <IconButton onClick={handleInfoClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              This table displays activity data across different time periods and modules.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              The table supports filtering by modules, sorting columns, and displays trends between periods.
              Use the tabs to switch between daily, weekly, and monthly views.
            </Typography>
          </Box>
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="h6" gutterBottom>Visual Indicators</Typography>
          
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box component="span" className={`${styles['color-box']} ${styles['highest-value-box']}`}></Box>
                    Highest Value
                  </Typography>
                  <Typography variant="body2">
                    Cells with the highest value in their column are highlighted with a light green background.
                    This helps identify the period with the most activity for each module.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <StarIcon sx={{ mr: 1, color: '#f44336' }} fontSize="small" />
                    Peak Value
                  </Typography>
                  <Typography variant="body2">
                    The red star indicates a statistically significant peak (more than 2 standard deviations above the mean).
                    These values represent unusual spikes in activity that stand out from typical patterns.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <TrendingUpIcon sx={{ mr: 1, color: '#4CAF50' }} fontSize="small" />
                    Upward Trend
                  </Typography>
                  <Typography variant="body2">
                    The green upward arrow indicates an increase in activity compared to the previous period.
                    Hover over these cells to see the percentage increase.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <TrendingDownIcon sx={{ mr: 1, color: '#F44336' }} fontSize="small" />
                    Downward Trend
                  </Typography>
                  <Typography variant="body2">
                    The red downward arrow indicates a decrease in activity compared to the previous period.
                    Hover over these cells to see the percentage decrease.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <TrendingFlatIcon sx={{ mr: 1, color: '#9E9E9E' }} fontSize="small" />
                    No Change
                  </Typography>
                  <Typography variant="body2">
                    The gray horizontal arrow indicates that activity remains unchanged from the previous period.
                    This means the values are exactly the same between consecutive time periods.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="body2" color="text.secondary">
            Note: You can sort any column by clicking on the column header. 
            The filter button allows you to show or hide specific modules in the table.
            {filteredData.length > 10 && (
              <Box component="span" sx={{ display: 'block', mt: 1 }}>
                <b>Important:</b> If there are more rows than can be displayed at once, you can scroll up and down to view additional data in the table.
              </Box>
            )}
          </Typography>

          {/* Add scrolling card for all users */}
          <Card variant="outlined" className={styles['scrolling-info']}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                Scrolling Navigation
              </Typography>
              <Typography variant="body2">
                The table supports scrolling vertically to view more time periods and horizontally to see all modules.
                Use mousewheel, trackpad gestures, or the scrollbar to navigate through larger datasets.
              </Typography>
            </CardContent>
          </Card>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleInfoClose} 
            variant="contained" 
            className={styles['primary-button']}
          >
            Got it
          </Button>
        </DialogActions>
      </Dialog>
      
      <Popover
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={handleFilterClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box sx={{ p: 1, maxHeight: 300, overflow: 'auto' }}>
          <Typography variant="subtitle2" sx={{ p: 1 }}>
            Filter Modules
          </Typography>
          {modules.map(module => (
            <MenuItem 
              key={module} 
              dense 
              onClick={() => handleFilterChange(module)}
            >
              <Checkbox 
                checked={moduleFilters[module] !== false}
                size="small"
                color="primary"
              />
              <ListItemText primary={module} />
            </MenuItem>
          ))}
        </Box>
      </Popover>
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs 
          value={granularity} 
          onChange={handleGranularityChange}
          aria-label="granularity tabs"
          sx={{ '& .MuiTab-root.Mui-selected': { color: '#28BBBB' }, '& .MuiTabs-indicator': { backgroundColor: '#28BBBB' } }}
        >
          <Tab 
            label={
              <Badge 
                badgeContent={dayCounts} 
                sx={{ "& .MuiBadge-badge": { backgroundColor: '#28BBBB', color: 'white' } }}
              >
                <span style={{ padding: '0 6px' }}>Daily</span>
              </Badge>
            } 
            value="daily" 
          />
          <Tab 
            label={
              <Badge 
                badgeContent={weekCounts} 
                sx={{ "& .MuiBadge-badge": { backgroundColor: '#28BBBB', color: 'white' } }}
              >
                <span style={{ padding: '0 6px' }}>Weekly</span>
              </Badge>
            } 
            value="weekly" 
          />
          <Tab 
            label={
              <Badge 
                badgeContent={monthCounts} 
                sx={{ "& .MuiBadge-badge": { backgroundColor: '#28BBBB', color: 'white' } }}
              >
                <span style={{ padding: '0 6px' }}>Monthly</span>
              </Badge>
            } 
            value="monthly" 
          />
        </Tabs>
      </Box>
      
      {filteredData.length > 0 ? (
        <TableContainer className={styles['scrollable-table-container']}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell 
                  onClick={() => handleColumnSort('date')}
                  sx={{ cursor: 'pointer', fontWeight: 'bold' }}
                >
                  Date 
                  {columnSortBy === 'date' && (
                    <span>{columnSortDir === 'desc' ? ' ▼' : ' ▲'}</span>
                  )}
                </TableCell>
                {visibleModules.map((module) => (
                  <TableCell 
                    key={module} 
                    align="center"
                    onClick={() => handleColumnSort(module)}
                    sx={{ cursor: 'pointer', fontWeight: 'bold' }}
                  >
                    {module}
                    {columnSortBy === module && (
                      <span>{columnSortDir === 'desc' ? ' ▼' : ' ▲'}</span>
                    )}
                  </TableCell>
                ))}
                <TableCell 
                  align="center" 
                  onClick={() => handleColumnSort('total')}
                  sx={{ cursor: 'pointer', fontWeight: 'bold' }}
                >
                  Total
                  {columnSortBy === 'total' && (
                    <span>{columnSortDir === 'desc' ? ' ▼' : ' ▲'}</span>
                  )}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.map((entry, rowIndex) => {
                // Calculate total for this row
                const rowTotal = entry.modules.reduce((sum, module) => sum + module.count, 0);
                
                // Find the previous period's data for trend calculation
                let previousEntry = null;
                if (rowIndex < filteredData.length - 1) {
                  previousEntry = filteredData[rowIndex + 1];
                }
                
                return (
                  <TableRow key={entry._id.group} hover>
                    <TableCell>{entry._id.group}</TableCell>
                    {visibleModules.map((moduleName) => {
                      const moduleData = entry.modules.find(m => m.module === moduleName);
                      const count = moduleData ? moduleData.count : 0;
                      
                      // Get previous period value for this module
                      const previousModuleData = previousEntry?.modules.find(
                        item => item.module === moduleName
                      );
                      const previousCount = previousModuleData ? previousModuleData.count : null;
                      
                      // Calculate trend
                      const trend = getTrend(count, previousCount);
                      const percentChange = getPercentChange(count, previousCount);
                      
                      // Check if this cell contains the highest value in the column
                      const isHighest = count > 0 && count === highestValues[moduleName];
                      
                      // Calculate module statistics for peak detection
                      // We don't need to calculate module average here as isPeak uses its own calculation
                      
                      // Check if this value is a peak
                      const peakStatus = isPeak(count, entry.modules);
                      
                      // Tooltip content
                      let tooltipContent = `${count} hits for ${moduleName} on ${entry._id.group}`;
                      if (trend === 'up' || trend === 'down') {
                        tooltipContent += ` (${trend === 'up' ? '+' : ''}${percentChange}% vs previous period)`;
                      } else if (trend === 'neutral' && previousCount !== null) {
                        tooltipContent += ` (unchanged vs previous period)`;
                      }
                      if (peakStatus) {
                        tooltipContent += `\nSignificant peak: value is significantly above the average`;
                      }
                      
                      return (
                        <Tooltip
                          key={`${entry._id.group}-${moduleName}`}
                          title={tooltipContent}
                          placement="top"
                          arrow
                        >
                          <StyledTableCell 
                            ishighest={isHighest.toString()} 
                            trend={trend}
                          >
                            <div className={styles['cell-content']}>
                              <span className={styles['cell-value']}>{count}</span>
                              {count > 0 && (
                                <>
                                  <span className={styles['trend-indicator']}>
                                    {trend === 'up' ? (
                                      <TrendingUpIcon fontSize="small" className={styles['trend-up-icon']} />
                                    ) : trend === 'down' ? (
                                      <TrendingDownIcon fontSize="small" className={styles['trend-down-icon']} />
                                    ) : (
                                      <TrendingFlatIcon fontSize="small" className={styles['trend-flat-icon']} />
                                    )}
                                  </span>
                                  {peakStatus && (
                                    <span style={{ 
                                      position: 'absolute', 
                                      top: '2px', 
                                      left: '2px', 
                                      color: '#f44336'
                                    }}>
                                      <StarIcon sx={{ fontSize: '0.7rem' }} />
                                    </span>
                                  )}
                                </>
                              )}
                            </div>
                          </StyledTableCell>
                        </Tooltip>
                      );
                    })}
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                      {rowTotal}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No data available for this time granularity
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default AllActivityTable;